import { useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { useCreateCoupon } from '@/hooks/useCoupons'
import { usePlans, usePackages } from '@/hooks/usePlans'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { CreateCouponRequest, CouponType, CouponAppliesTo } from '@/types'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { getApiErrorMessage } from '@/lib/utils'

export const CouponCreatePage = () => {
  const navigate = useNavigate()
  const createCoupon = useCreateCoupon()
  const { data: plans } = usePlans()
  const { data: packages } = usePackages()

  const [form, setForm] = useState<CreateCouponRequest>({
    code: '',
    type: 'percentage',
    value: 0,
    applies_to: 'both',
    plan_ids: [],
    package_ids: [],
    max_uses: undefined,
    max_uses_per_user: undefined,
    min_amount: 0,
    max_discount: undefined,
    starts_at: new Date().toISOString().split('T')[0],
    expires_at: undefined,
    notes: '',
  })

  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([])
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([])

  const togglePlan = (id: string) => {
    setSelectedPlanIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]))
  }

  const togglePkg = (id: string) => {
    setSelectedPackageIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    )
  }

  const handleSubmit = async () => {
    if (form.expires_at && form.starts_at > form.expires_at) {
      toast.error('La fecha de inicio no puede ser posterior a la fecha de expiracion')
      return
    }
    try {
      const payload: CreateCouponRequest = {
        ...form,
        plan_ids: selectedPlanIds.length ? selectedPlanIds : undefined,
        package_ids: selectedPackageIds.length ? selectedPackageIds : undefined,
        starts_at: new Date(form.starts_at).toISOString(),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
      }
      await createCoupon.mutateAsync(payload)
      navigate({ to: '/coupons' })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al crear cupon'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/coupons">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Cupones
          </Link>
        </Button>
      </div>

      <h1 className="text-3xl font-bold">Crear Cupon</h1>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Informacion basica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label>Codigo</Label>
              <Input
                placeholder="DESCUENTO20"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={50}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Tipo</Label>
                <Select
                  value={form.type}
                  onValueChange={(v) => setForm((f) => ({ ...f, type: v as CouponType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje</SelectItem>
                    <SelectItem value="fixed">Monto fijo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Valor {form.type === 'percentage' ? '(%)' : '(USD)'}</Label>
                <Input
                  type="number"
                  min={0}
                  step={form.type === 'percentage' ? 1 : 0.01}
                  max={form.type === 'percentage' ? 100 : undefined}
                  value={form.value}
                  onChange={(e) => setForm((f) => ({ ...f, value: Number(e.target.value) }))}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Aplica a</Label>
              <Select
                value={form.applies_to}
                onValueChange={(v) => setForm((f) => ({ ...f, applies_to: v as CouponAppliesTo }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subscription">Suscripciones</SelectItem>
                  <SelectItem value="documents">Documentos</SelectItem>
                  <SelectItem value="both">Ambos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Notas (opcional)</Label>
              <Input
                placeholder="Notas internas..."
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                maxLength={500}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Limites y fechas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Max usos totales</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="Sin limite"
                  value={form.max_uses ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      max_uses: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Max usos por usuario</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="Sin limite"
                  value={form.max_uses_per_user ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      max_uses_per_user: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Monto minimo (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.min_amount}
                  onChange={(e) => setForm((f) => ({ ...f, min_amount: Number(e.target.value) }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Descuento max (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="Sin limite"
                  value={form.max_discount ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      max_discount: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Inicio</Label>
                <Input
                  type="date"
                  value={form.starts_at}
                  onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Expiracion (opcional)</Label>
                <Input
                  type="date"
                  value={form.expires_at ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, expires_at: e.target.value || undefined }))
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {(form.applies_to === 'subscription' || form.applies_to === 'both') && plans?.length ? (
          <Card>
            <CardHeader>
              <CardTitle>Planes (opcional)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-xs text-muted-foreground">
                Si no seleccionas ninguno, aplica a todos los planes.
              </p>
              <div className="space-y-2">
                {plans.map((plan) => (
                  <label key={plan.id} className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={selectedPlanIds.includes(plan.id)}
                      onCheckedChange={() => togglePlan(plan.id)}
                    />
                    <span className="text-sm">{plan.display_name || plan.name}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {(form.applies_to === 'documents' || form.applies_to === 'both') && packages?.length ? (
          <Card>
            <CardHeader>
              <CardTitle>Paquetes (opcional)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-xs text-muted-foreground">
                Si no seleccionas ninguno, aplica a todos los paquetes.
              </p>
              <div className="space-y-2">
                {packages
                  .filter((p) => p.is_active)
                  .map((pkg) => (
                    <label key={pkg.id} className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={selectedPackageIds.includes(pkg.id)}
                        onCheckedChange={() => togglePkg(pkg.id)}
                      />
                      <span className="text-sm">
                        {pkg.name} ({pkg.document_count} docs)
                      </span>
                    </label>
                  ))}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" asChild>
          <Link to="/coupons">Cancelar</Link>
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={createCoupon.isPending || !form.code || !form.value}
        >
          {createCoupon.isPending ? 'Creando...' : 'Crear cupon'}
        </Button>
      </div>
    </div>
  )
}
